import {
  Chip,
  Colors,
  Flex,
  GoodsCarousel as UIKitGoodsCarousel,
  GoodsCarouselSkeleton,
  List,
  RemoteImage,
  Typography,
  useScreenSize,
} from '@banhala/ui-kit';
import type { GoodsCarouselProps } from './types';

import { bridge, isNotNil, useNavigator } from '../../../../utils';

import { useLogOnGoodsPressItem } from './log/logOnPressItem';
import type { GoodsCoreModel, IScreensLogging } from '../../../../api/types';
import { useLogOnPressLike } from './log/logOnPressLike';
import {
  useAddLikeGoods,
  useOpenDeeplink,
  useSafeTimeout,
} from '../../../../hooks';
import { useLogDisplayGoods } from './log/useLogDisplayGoods';
import { useLogDisplayCarousel } from './log/useLogDisplayCarousel';
import { useLogDisplayBanner } from './log/useLogDisplayBanner';
import { useLogClickBanner } from './log/useLogClickBanner';
import { useLogClickFilter } from '../FilterList/hooks/useLogClickFilter';
import { useLogApplyFilter } from '../FilterList/hooks/useLogApplyFilter';
import { getGoodsCarouselData } from './goodsCarouselDataConverter';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useRemoveLikeGoods } from '../../../../queries';
import { AblyGoodsCardSize } from '../../../../enums';
import { useAnalyticsContext } from '../../../../context';

const VIEWABILITY_CONFIG = { itemVisiblePercentThreshold: 50 };

/**
 *
 * @example
 * <AtomicRenderer data={atomicRendererDataConverter(mockHeadlineSmallOneLineData)} />
 * // mockHeadlineSmallOneLineData를 사용하면 GoodsCarousel까지 확인할 수 있습니다.
 */
export const GoodsCarousel = ({
  entity,
  likeMap,
  setLikeMap,
  screenName,
  isFetching = false,
  atomicParams = {},
  onChipPress,
  initParams,
  ...restProps
}: GoodsCarouselProps) => {
  const { navigator } = useNavigator();
  const { openDeeplink } = useOpenDeeplink();
  const { width: screenWidth } = useScreenSize();
  const setSafeTimeout = useSafeTimeout();
  const logOnPressLike = useLogOnPressLike();
  const logOnGoodsPressItem = useLogOnGoodsPressItem();
  const radio = useMemo(
    () => restProps.body?.item.radio ?? null,
    [restProps.body],
  );
  const subRadio = useMemo(
    () => restProps.body?.item.sub_radio ?? null,
    [restProps.body],
  );
  const banner = useMemo(
    () => restProps.body?.item.banner ?? null,
    [restProps.body],
  );

  // iOS: carouselCardSize = renderListCardSize(renders) — render.size 기반 결정
  const carouselSize = useMemo((): 'medium' | 'small' => {
    const firstSize = entity?.[0]?.render?.size;
    if (firstSize === AblyGoodsCardSize.Small) return 'small';
    return 'medium';
  }, [entity]);

  // iOS: ProductRenderFactory imageRatio = render.data.image.ratio — 서버값으로
  // 카드 이미지 비율 결정. ratio를 카드에 전달하지 않으면 ui-kit Thumbnail이 1.2
  // (직사각)로 fallback해 정사각(1.0) 캐러셀(예: related_goods)이 직사각으로
  // 렌더되던 문제를 보정. ui-kit ratio는 1 | 1.2만 허용하므로 1.0은 1, 그 외는 1.2.
  const carouselRatio = useMemo((): 1 | 1.2 => {
    return entity?.[0]?.render?.data?.image?.ratio === 1 ? 1 : 1.2;
  }, [entity]);

  useEffect(() => {
    initParams?.({
      ...(radio ? { [radio.query_name]: radio.selected_value } : {}),
      ...(subRadio ? { [subRadio.query_name]: subRadio.selected_value } : {}),
    });
  }, [radio, subRadio, initParams]);

  const goodsData = getGoodsCarouselData({ entity });

  useEffect(() => {
    setLikeMap(prevState => {
      return {
        ...prevState,
        ...entity?.reduce<Record<string, boolean>>((acc, cur) => {
          acc[cur.item.sno] =
            prevState[cur.item.sno] ?? cur.item.like?.is_liked ?? false;
          return acc;
        }, {}),
      };
    });
  }, [entity, setLikeMap]);
  const { mutateAsync: mutateRemoveLikeGoods } = useRemoveLikeGoods();
  const { logDisplayGoods, resetDisplayGoods } = useLogDisplayGoods(
    restProps.isVisible,
    restProps.isAwaitingDataChange,
  );
  const { resetDisplayCarousel } = useLogDisplayCarousel(
    restProps.logging,
    restProps.isVisible,
    restProps.isAwaitingDataChange,
  );

  // 외곽 필터(chip 등) 적용 시 isPendingScroll true→false 전이가 fetch 완료 +
  // scrollToFilterList 300ms 대기 후 scroll dispatch 시점을 의미. 이 전이를
  // 기준으로 scroll 애니메이션(~500ms) 버퍼 후 reset.
  //
  // 단, pending 구간 중 캐러셀이 refetch되어 entity 레퍼런스가 바뀌면
  // [logging] effect와 onViewableItemsChanged의 자연 전송이 이미 DISPLAY
  // 이벤트를 처리하므로, refetch가 없었던 경우(레퍼런스 동일)에만 명시적
  // reset을 호출해 중복 전송을 방지.
  //
  // wasPendingScrollRef 초기값이 null인 이유: 외곽 PROXY URL이 변하면
  // GoodsCarousel이 pending 도중 새로 mount되는데, 첫 effect run의
  // isPendingScroll=true를 false→true 전이로 오인하면 후속 false 전이에서
  // 방금 마운트된 entity가 그대로 보존되어 reset이 발동, 자연 mount 전송과
  // 합쳐져 DISPLAY_CAROUSEL이 2회 전송됨.
  const wasPendingScrollRef = useRef<boolean | null>(null);
  const entityAtPendingStartRef = useRef<typeof entity>(undefined);
  useEffect(() => {
    const isPendingScroll = restProps.isPendingScroll ?? false;
    const wasPendingScroll = wasPendingScrollRef.current;
    wasPendingScrollRef.current = isPendingScroll;

    // 첫 effect run: 초기 상태만 기록하고 transition 처리는 skip.
    if (wasPendingScroll === null) return;

    if (!wasPendingScroll && isPendingScroll) {
      entityAtPendingStartRef.current = entity;
      return;
    }

    if (!(wasPendingScroll && !isPendingScroll)) return;
    if (entityAtPendingStartRef.current !== entity) return;

    const timer = setSafeTimeout(() => {
      resetDisplayGoods();
      resetDisplayCarousel();
    }, 500);
    return () => clearTimeout(timer);
  }, [
    restProps.isPendingScroll,
    entity,
    resetDisplayGoods,
    resetDisplayCarousel,
    setSafeTimeout,
  ]);

  useLogDisplayBanner(banner, restProps.logging, restProps.isVisible);
  const { screen } = useAnalyticsContext();

  const logClickBanner = useLogClickBanner(banner, restProps.logging);
  const handlePressBanner = useCallback(() => {
    if (!banner) return;
    logClickBanner();
    openDeeplink(banner.deeplink);
  }, [banner, logClickBanner, openDeeplink]);

  // iOS MetaFeedRadioFilterGroupReactor.updateRadioFilterSelectionInMasterReactor 와 동일.
  // radio: CLICK_FILTER + APPLY_FILTER, sub_radio: CLICK_SUB_FILTER + APPLY_FILTER
  const logClickFilter = useLogClickFilter(restProps.logging ?? {});
  const logApplyFilter = useLogApplyFilter(restProps.logging ?? {});
  const handleRadioPress = useCallback(
    (queryName: string, queryValue: string, itemLogging: IScreensLogging) => {
      if (
        screen === 'BRAND_DEPARTMENT' ||
        screen === 'BEAUTY_DEPARTMENT'
        // screen === 'LIFE_PARENT_CATEGORY_DEPARTMENT' ||
        // screen === 'JAPAN_DEPARTMENT' ||
        // screen === 'SHOPPINGMALL_DEPARTMENT'
      ) {
        logClickFilter({ itemLogging, eventName: 'CLICK_CAROUSEL_FILTER' });
      } else {
        logClickFilter({ itemLogging, eventName: 'CLICK_FILTER' });
        logApplyFilter({ itemLogging });
      }
      onChipPress?.(queryName, queryValue);
    },
    [logClickFilter, logApplyFilter, onChipPress, screen],
  );
  const handleSubRadioPress = useCallback(
    (queryName: string, queryValue: string, itemLogging: IScreensLogging) => {
      logClickFilter({ itemLogging, eventName: 'CLICK_SUB_FILTER' });
      logApplyFilter({ itemLogging });
      onChipPress?.(queryName, queryValue);
    },
    [logClickFilter, logApplyFilter, onChipPress],
  );

  const { addLikeGoods } = useAddLikeGoods({
    listParams: entity?.[0]?.logging.analytics?.LIST_ID as string | undefined,
    onChangeIsLiked: (sno, nextValue) => {
      setLikeMap(prevState => ({ ...prevState, [sno]: nextValue }));
    },
    screen:
      screenName ??
      (entity?.[0]?.logging.analytics?.SCREEN as string | undefined) ??
      '',
  });

  if (!entity) {
    return null;
  }

  const handlePressItem = async ({
    logging,
    item,
  }: {
    logging: IScreensLogging;
    item: GoodsCoreModel;
  }) => {
    logOnGoodsPressItem({ item, logging });
    navigator.moveToGoods({
      sno: item.sno,
      firstPageRendering: item.first_page_rendering ?? null,
      remoteDeeplink: item.remote_deeplink ?? undefined,
      loggingAnalytics: logging.analytics,
      ad: item.ad,
    });
  };

  const handlePressLike = async ({
    logging,
    item,
  }: {
    logging: IScreensLogging;
    item: GoodsCoreModel;
  }) => {
    const currentLike = likeMap[item.sno] ?? false;
    logOnPressLike({ logging, item, currentLike });
    if (currentLike) {
      await mutateRemoveLikeGoods({
        sno: item.sno,
        list_params_referrer: logging.analytics?.LIST_ID as string | undefined,
      });
      bridge.likeGoods({
        goods_sno: item.sno,
        is_liked: false,
      });
      setLikeMap(prevState => ({ ...prevState, [item.sno]: false }));
      return;
    }
    addLikeGoods(item.sno, logging.analytics, logging.ably_only_log);
  };

  return (
    <Flex>
      {isNotNil(radio) && radio.possible_items.length > 0 && (
        <List
          horizontal={true}
          contentContainerStyle={getRadioContainerStyle(radio.view_type)}
          data={radio.possible_items}
          extraData={atomicParams}
          showsHorizontalScrollIndicator={false}
          style={{
            borderBottomWidth: radio?.view_type === 'TAB' ? 1 : undefined,
            borderColor: Colors.border_tertiary,
          }}
          keyExtractor={item => item.query_value}
          renderItem={({ item }) => {
            const isSelected =
              atomicParams[radio.query_name] === item.query_value;

            if (radio?.view_type === 'HEADLINE_TAB') {
              return (
                <Flex.Row
                  gap={4}
                  alignItems={'center'}
                  borderBottomWidth={isSelected ? 2 : 1}
                  borderColor={
                    isSelected ? 'content_primary' : 'border_tertiary'
                  }
                  padding={{ horizontal: 4, top: 4, bottom: 10 }}
                  onPress={() =>
                    handleRadioPress(
                      radio.query_name,
                      item.query_value,
                      item.logging,
                    )
                  }
                >
                  <Typography
                    type={'h5'}
                    color={isSelected ? 'content_primary' : 'content_tertiary'}
                  >
                    {item.text}
                  </Typography>
                  {isNotNil(item.image) &&
                    isNotNil(item.image_width) &&
                    isNotNil(item.image_height) && (
                      <RemoteImage
                        uri={item.image}
                        width={item.image_width}
                        height={item.image_height}
                      />
                    )}
                </Flex.Row>
              );
            }
            if (radio?.view_type === 'TAB') {
              return (
                <Flex.Row
                  gap={4}
                  alignItems={'center'}
                  borderBottomWidth={isSelected ? 2 : 0}
                  borderColor={isSelected ? 'content_primary' : 'white'}
                  padding={{ horizontal: 2, vertical: 13 }}
                  onPress={() =>
                    handleRadioPress(
                      radio.query_name,
                      item.query_value,
                      item.logging,
                    )
                  }
                >
                  <Typography
                    type={isSelected ? 'subtitle2' : 'body2'}
                    color={isSelected ? 'content_primary' : 'content_secondary'}
                  >
                    {item.text}
                  </Typography>
                  {isNotNil(item.image) &&
                    isNotNil(item.image_width) &&
                    isNotNil(item.image_height) && (
                      <RemoteImage
                        uri={item.image}
                        width={item.image_width}
                        height={item.image_height}
                      />
                    )}
                </Flex.Row>
              );
            }
            return (
              <Chip
                state={isSelected ? 'active' : 'enabled'}
                size={'medium'}
                onPress={() =>
                  handleRadioPress(
                    radio.query_name,
                    item.query_value,
                    item.logging,
                  )
                }
              >
                {item.text}
              </Chip>
            );
          }}
        />
      )}
      {isNotNil(subRadio) && subRadio.possible_items.length > 0 && (
        <List
          horizontal={true}
          contentContainerStyle={{
            paddingVertical: 12,
            paddingHorizontal: 16,
            gap: 8,
          }}
          data={subRadio.possible_items}
          extraData={atomicParams}
          showsHorizontalScrollIndicator={false}
          keyExtractor={item => item.query_value}
          renderItem={({ item }) => {
            const isSelected =
              atomicParams[subRadio.query_name] === item.query_value;

            return (
              <Flex.Row
                justifyContent={'center'}
                alignItems={'center'}
                padding={{
                  vertical: isNotNil(item.image) ? 4 : 10,
                  left: isNotNil(item.image) ? 4 : 12,
                  right: 12,
                }}
                borderWidth={1}
                borderColor={isSelected ? 'content_primary' : 'border_tertiary'}
                borderRadius={32}
                gap={4}
                onPress={() =>
                  handleSubRadioPress(
                    subRadio.query_name,
                    item.query_value,
                    item.logging,
                  )
                }
              >
                {isNotNil(item.image) && (
                  <RemoteImage
                    uri={item.image}
                    width={28}
                    height={28}
                    borderRadius={'xlarge'}
                  />
                )}
                <Typography
                  type={'subtitle3'}
                  color={isSelected ? 'content_primary' : 'content_secondary'}
                >
                  {item.text}
                </Typography>
              </Flex.Row>
            );
          }}
        />
      )}
      {isNotNil(banner) &&
        (isFetching ? (
          <Flex
            width={screenWidth}
            height={screenWidth * banner.thumbnail_ratio}
            backgroundColor={'bg_tertiary'}
            margin={{ bottom: 12 }}
          />
        ) : (
          <Flex onPress={handlePressBanner} margin={{ bottom: 12 }}>
            <RemoteImage
              uri={banner.thumbnail}
              width={screenWidth}
              height={screenWidth * banner.thumbnail_ratio}
            />
          </Flex>
        ))}
      {isFetching ? (
        <GoodsCarouselSkeleton count={4} />
      ) : (
        <UIKitGoodsCarousel
          likeMap={likeMap}
          listTestID={
            entity?.[0]?.logging.analytics?.LIST_ID as string | undefined
          }
          onPressLike={handlePressLike}
          size={carouselSize}
          ratio={carouselRatio}
          showRanking={entity.some(item => isNotNil(item.render?.data.ranking))}
          list={goodsData}
          onPressItem={async ({ item, logging }) => {
            handlePressItem({ logging, item });
          }}
          keyExtractor={(item, index) => (item.sno ?? index).toString()}
          onViewableItemsChanged={logDisplayGoods}
          viewabilityConfig={VIEWABILITY_CONFIG}
        />
      )}
    </Flex>
  );
};

/**
 * iOS `RadioFilterType.sectionInset` / `minimumInteritemSpacing` + Android
 * `RadioFilterViewType.marginStartEnd` / `marginCenter` 기준.
 */
const getRadioContainerStyle = (viewType: 'CHIP' | 'HEADLINE_TAB' | 'TAB') => {
  if (viewType === 'TAB') {
    return { paddingHorizontal: 16, gap: 20 };
  }
  if (viewType === 'HEADLINE_TAB') {
    return { paddingHorizontal: 12, gap: 2 };
  }
  return { paddingTop: 8, paddingBottom: 12, paddingHorizontal: 16, gap: 8 };
};
